import { Outlet, useLocation, useRoutes } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import "../../css/Layout.css";
import Footer from "./Footer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLayout } from "../../context/LayoutContext";
import { protectedPageRoutes } from "../../routes/protectedPageRoutes";

const MAX_MOUNTED_TABS = 5;

function TabRouteView({ link, active }) {
  const element = useRoutes(protectedPageRoutes, link);

  return (
    <div className="tab-panel" hidden={!active} aria-hidden={!active}>
      {element}
    </div>
  );
}

export default function Layout() {
  const { tabs, activeTabLink, closeTab, handleTabClick } = useLayout();
  const location = useLocation();
  const currentRouteIsCached = tabs.some((tab) => tab.link === location.pathname);
  const [recentTabLinks, setRecentTabLinks] = useState(() =>
    activeTabLink ? [activeTabLink] : []
  );
  const tabRefs = useRef(new Map());

  useEffect(() => {
    if (!activeTabLink) return;

    setRecentTabLinks((previous) => [
      ...previous.filter((link) => link !== activeTabLink),
      activeTabLink,
    ].slice(-MAX_MOUNTED_TABS));
  }, [activeTabLink]);

  useEffect(() => {
    tabRefs.current.get(activeTabLink)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeTabLink]);

  const mountedTabs = useMemo(() => {
    const mountedLinks = new Set(recentTabLinks);
    if (activeTabLink) mountedLinks.add(activeTabLink);
    return tabs.filter((tab) => mountedLinks.has(tab.link));
  }, [activeTabLink, recentTabLinks, tabs]);

  return (
    <div className="layout">
      <Sidebar />
      <div className="layout-content">
        <Navbar />

        {tabs.length > 0 && (
          <div className="tab-bar">
            {tabs.map(tab => (
              <div
                className={`tab ${tab.link === activeTabLink ? "active" : ""}`}
                key={tab.link}
                ref={(element) => {
                  if (element) tabRefs.current.set(tab.link, element);
                  else tabRefs.current.delete(tab.link);
                }}
              >
                <span
                  className="tab-name"
                  onClick={() => handleTabClick(tab.link)}
                  title={tab.name}
                >
                  {tab.name}
                </span>

                <button className="tab-close" onClick={() => closeTab(tab.link)}>×</button>
              </div>
            ))}
          </div>
        )}

        <main className={`main-content ${tabs.length > 0 ? "card-style" : ""}`}>
          {mountedTabs.map((tab) => (
            <TabRouteView
              key={tab.link}
              link={tab.link}
              active={currentRouteIsCached && tab.link === activeTabLink}
            />
          ))}
          {!currentRouteIsCached && <Outlet />}
        </main>

        <Footer />
      </div>
    </div>
  );
}
